import { Col, Grid, Row, Spin, Statistic as AntStatistic, StatisticProps } from "antd";
import React from "react";
import { useQuery } from "react-query";
import { colors } from "src/constants";
import { api } from "src/utils";
import slotLogo from "src/assets/logo/slot.svg";
import betLogo from "src/assets/logo/bet.svg";
import sportLogo from "src/assets/logo/sport.svg";

// Brand wordmarks render white on the purple bonus box (filter inverts the purple SVG).
const productLogoStyle: React.CSSProperties = {
  height: 12,
  filter: "brightness(0) invert(1)",
};

/** compact: smaller text, so the bonus breakdown still fits one line on a phone. */
export function Statistic({ title, compact = false, ...props }: StatisticProps & { compact?: boolean }) {
  return (
    <AntStatistic
      {...props}
      title={<div style={{ color: colors.gray1, opacity: 0.6, fontSize: compact ? 12 : undefined }}>{title}</div>}
      valueStyle={{ color: "white", fontSize: compact ? 15 : 18, fontWeight: 700 }}
      precision={2}
    />
  );
}

export interface CustomerStatisticsProps {
  customerCode: string;
}

export type CustomerStatisticsHandler = {
  refetchBalance: () => void;
};

const CustomerStatisticsComponent: React.ForwardRefRenderFunction<CustomerStatisticsHandler, CustomerStatisticsProps> = ({ customerCode }, ref) => {
  const isSmallScreen = Grid.useBreakpoint().md === false;
  const { data, isFetching, refetch } = useQuery(
    ["customerBalance", customerCode],
    async () => {
      const response = await api.get(`payment/customer/${customerCode}/balance`);
      // Balance endpoint also returns the per-product bonus breakdown (camelCased by the interceptor).
      return response.data.data as {
        cashBalance: number;
        coinBalance: number;
        totalBalance: number;
        pigSpinCoinAmount?: number;
        pigBetCoinAmount?: number;
        pigSportCoinAmount?: number;
      };
    },
    { keepPreviousData: true }
  );

  React.useImperativeHandle(ref, () => ({
    refetchBalance: () => {
      refetch();
    }
  }));

  if (!data) return null;

  return (
    <Spin spinning={isFetching}>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={24}>
          <div
            style={{
              borderRadius: 8,
              padding: isSmallScreen ? 8 : "8px 16px",
              background: "linear-gradient(225deg, #9737C1 0%, #5B4ABF 100%)",
            }}
          >
            <h3 style={{ color: "white", marginBottom: isSmallScreen ? 4 : undefined }}>ยอดเงินในกระเป๋าปัจจุบัน (THB)</h3>
            {/* Wraps on phones: the bonus box moves under the totals. */}
            <Row gutter={[16, 8]} align="middle">
              {/* total + cash */}
              <Col flex="none">
                <Statistic title="ยอดเงินรวม" value={data?.totalBalance} />
              </Col>
              <Col flex="none">
                <Statistic title="กระเป๋าเงินสด" value={data?.cashBalance} />
              </Col>
              {/* spacer: push the bonus box to the right */}
              {!isSmallScreen && <Col flex="auto" />}
              {/* bonus box: total bonus + SLOT/BET/SPORT breakdown; a full-width line of its own on phones */}
              <Col flex={isSmallScreen ? undefined : "none"} span={isSmallScreen ? 24 : undefined}>
                <div
                  style={{
                    borderRadius: 8,
                    padding: isSmallScreen ? "4px 6px" : "4px 16px",
                    background: "rgba(255, 255, 255, 0.12)",
                  }}
                >
                  <Row
                    gutter={isSmallScreen ? 6 : 16}
                    align="middle"
                    justify={isSmallScreen ? "space-between" : undefined}
                    wrap={false}
                  >
                    <Col flex="none">
                      <Statistic compact={isSmallScreen} title="กระเป๋าโบนัสรวม" value={data?.coinBalance} />
                    </Col>
                    <Col flex="none">
                      <div
                        style={{
                          width: 1,
                          height: 40,
                          background: "rgba(255, 255, 255, 0.3)",
                        }}
                      />
                    </Col>
                    <Col flex="none">
                      <Statistic
                        compact={isSmallScreen}
                        title={<img src={slotLogo} alt="SLOT" style={productLogoStyle} />}
                        value={data?.pigSpinCoinAmount}
                      />
                    </Col>
                    <Col flex="none">
                      <Statistic
                        compact={isSmallScreen}
                        title={<img src={betLogo} alt="BET" style={productLogoStyle} />}
                        value={data?.pigBetCoinAmount}
                      />
                    </Col>
                    <Col flex="none">
                      <Statistic
                        compact={isSmallScreen}
                        title={<img src={sportLogo} alt="SPORT" style={productLogoStyle} />}
                        value={data?.pigSportCoinAmount}
                      />
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
    </Spin>
  );
}

export const CustomerStatistics = React.forwardRef(CustomerStatisticsComponent);
export default CustomerStatistics;
